import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';
import debounce from 'lodash.debounce';
import { useSpring, animated } from 'react-spring';
import { Mentions } from 'antd';
import feedSocket from '../../../../shared/utils/setFeedSocketConfig';
import TBButton from '../../../../shared/components/Button/Button';
import GiphyEmojiButtons from './GiphyEmojiButtons';
import SendButton from './SendButton';

import { setUploadMediaFalse } from '../../ducks/feedActions';
import useFeed from '../hooks/useFeed';
import {
  urlSnapper,
  userParser,
  buildSubmissionObject,
  findFeedIndex,
  findCommentIndex,
  filterUserMentions,
  replaceMentionsInMessage,
} from '../../helperFunctions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const AnimatedMediaBoxDiv = styled(animated.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-top: 0;
  margin-left: 0px;
  left: 0px;
  min-height: 30px;
  min-width: 30px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-image: ${(props) => `url(${props.image})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border: 0px solid #ced4da;
  border-radius: 50%;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

const FormGroup = styled('div')`
  width: 100%;
  .MuiOutlinedInput-multiline {
    padding: 7px;
  }
  .MuiInputBase-multiline.MuiInputBase-marginDense {
    padding: 7px;
  }
  .MuiOutlinedInput-multiline.MuiOutlinedInput-marginDense {
    padding: 7px;
  }
  .MuiOutlinedInput-inputMarginDense {
    padding-left: 5px !important;
    margin-top: 2px !important;
  }
`;

const FormWrapper = styled('div')`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const MediaBox = styled('div')`
  margin-right: ${(props) => (props.activeMediaBarOb[props.commentObjectId] && props.uploadMedia
    ? '15px'
    : '0px')};
  .remove-media {
    display: ${(props) => (props.uploadMedia ? 'flex' : 'none')};
    border-radius: 50%;
    height: 16px;
    width: 16px;
    position: absolute;
    left: 25px;
    z-index: 100;
    top: -5px;
    background: #d6d6d6;
  }
`;

const NewReplyBoxWrap = styled('div')`
  width: 98%;
  display: flex;
  .send-button {
    display: flex;
    width: 80px;
  }
`;

const NewReplyInput = styled('div')`
  display: flex;
  align-items: center;
  position: relative;
  .text-area {
    width: 100%;
    white-space: pre-line;
    padding-right: 100px;
    border-radius: 10px;
    padding: 4px 0px;
  }
`;

const TextActionsContainer = styled('div')`
  display: flex;
  align-items: center;
  position: absolute;
  right: 10px;
`;

const NewReplyBox = ({
  feedObject,
  commentObject,
  isCommentClicked,
  showReplies,
  setShowReplies,
}) => {
  const { handleCreateFeedPostCommentReply } = useFeed();

  const [userMentions, setUserMentions] = useState([]);

  const dispatch = useDispatch();
  const activeMediaBarOb = useSelector(
    (state) => state.feed.data.activeMediaBarOb,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const feed = useSelector((state) => state.feed.data.feed);
  const isReplyActivated = useSelector(
    (state) => state.feed.data.isReplyActivated[commentObject.id],
  );
  const pinnedFeed = useSelector((state) => state.feed.data.pinnedFeed);
  const uploadMedia = useSelector((state) => state.feed.data.uploadMedia);
  const users = useSelector((state) => state.feed.data.users);

  const commentObjectId = commentObject.id ? commentObject.id : 0;
  const mediaBoxStyle = useSpring({
    opacity: uploadMedia ? '1' : '0',
    minWidth: uploadMedia ? '30px' : '0px',
    maxWidth: uploadMedia ? '30px' : '0px',
  });
  const replyBox = useSpring({
    width: activeMediaBarOb[commentObjectId] && uploadMedia ? '90%' : '100%',
  });
  const submitDisabled = (messageBody) => !uploadMedia.id && !messageBody?.trim().length;

  const handleIsTyping = debounce(
    () => {
      feedSocket.emit('isTyping', {
        postId: feedObject.id,
        accountCode: currentUser.accountCode,
        userId: currentUser.id,
        isTyping: true,
      });
    },
    1000,
    {
      leading: true,
      trailing: false,
    },
  );

  const handleMentionsChange = (setFieldValue, string) => {
    setFieldValue('messageBody', string);
    filterUserMentions(userMentions, setUserMentions, string);
    handleIsTyping();
  };

  const handleMentionsKeyPress = (e, submitForm) => {
    const { key, shiftKey } = e;
    if (key === 'Enter' && !shiftKey) {
      e.preventDefault();
      submitForm();
    }
  };

  const handleMentionsSelect = (selection) => {
    if (selection && selection.value) {
      // Find the user that matches the selected value
      const selectedUser = users.find((user) => `${user.first} ${user.last}` === selection.value);

      if (selectedUser) {
        const submissionValue = `[u=${selectedUser.id}|${selectedUser.first} ${selectedUser.last}]`;
        setUserMentions((prevUserMentions) => [
          ...prevUserMentions,
          submissionValue,
        ]);
      }
    }
  };

  const handleSubmit = (values) => {
    if (submitDisabled(values.messageBody)) return;
    const { messageBody } = values;
    const processedText = messageBody
      ? replaceMentionsInMessage(messageBody, userMentions)
      : '';
    const submissionOb = buildSubmissionObject(processedText, uploadMedia);

    const feedObjectIndex = findFeedIndex(feedObject, feed, pinnedFeed);
    const commentObjectIndex = findCommentIndex(commentObject, feedObject);

    handleCreateFeedPostCommentReply({
      accountCode: currentUser.accountCode,
      submissionOb,
      feedObjectIndex,
      feedObjectId: feedObject.id,
      commentObjectIndex,
      commentObjectId: commentObject.id,
      pinned: feedObject.pinned,
      onSuccess: () => {
        if (!showReplies) {
          setShowReplies(true);
        }
      },
    });
    feedTracker('Normal Mode', 'Reply created');
  };

  useEffect(() => {
    if (isCommentClicked) {
      const selector = document.getElementById(`${feedObject.id}`);
      if (selector) {
        selector.focus({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [isCommentClicked]);

  useEffect(() => {
    if (isReplyActivated) {
      const selector = document.getElementById(`${commentObject.id}`);
      if (selector) {
        selector.focus({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [isReplyActivated]);

  const renderMediaBox = () => (
    <MediaBox
      activeMediaBarOb={activeMediaBarOb}
      commentObjectId={commentObjectId}
      uploadMedia={uploadMedia}
      image={urlSnapper(uploadMedia)}
    >
      <AnimatedMediaBoxDiv
        style={mediaBoxStyle}
        image={urlSnapper(uploadMedia)}
      >
        <TBButton
          icon='remove'
          iconOnly
          square
          onClick={() => {
            dispatch(setUploadMediaFalse());
          }}
          className='remove-media'
        />
      </AnimatedMediaBoxDiv>
    </MediaBox>
  );

  const renderMentionsInput = (
    values,
    isSubmitting,
    submitForm,
    setFieldValue,
  ) => (
    <Mentions
      className='text-area'
      placement='bottom'
      placeholder='Reply to comment'
      value={values.messageBody}
      onSelect={handleMentionsSelect}
      disabled={isSubmitting || !feedObject.replyAllowed}
      id={commentObject ? commentObject.id : ''}
      name='messageBody'
      onKeyDown={(e) => handleMentionsKeyPress(e, submitForm)}
      onChange={(string) => {
        handleMentionsChange(setFieldValue, string);
      }}
    >
      {users && !!users.length && userParser(users)}
    </Mentions>
  );

  const renderTextActions = (
    values,
    setSubmitting,
    submitForm,
    setFieldValue,
  ) => (
    <TextActionsContainer>
      <GiphyEmojiButtons
        setFieldValue={setFieldValue}
        values={values}
        commentId={commentObject.id}
        fieldName='messageBody'
      />
      <SendButton
        onClick={() => {
          setSubmitting(true);
          submitForm(values);
        }}
        disabled={submitDisabled(values.messageBody)}
        className='send-button'
      />
    </TextActionsContainer>
  );

  return (
    <Formik
      initialValues={{
        messageBody: '',
      }}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
        setUploadMediaFalse();
        resetForm();
        setSubmitting(false);
      }}
    >
      {({
        submitForm,
        setFieldValue,
        values,
        setSubmitting,
        isSubmitting,
      }) => (
        <FormWrapper>
          {commentObject
            && activeMediaBarOb[commentObjectId]
            && renderMediaBox()}
          <animated.div style={replyBox}>
            <NewReplyBoxWrap>
              <FormGroup>
                <NewReplyInput>
                  {renderMentionsInput(
                    values,
                    isSubmitting,
                    submitForm,
                    setFieldValue,
                  )}
                  {renderTextActions(
                    values,
                    setSubmitting,
                    submitForm,
                    setFieldValue,
                  )}
                </NewReplyInput>
              </FormGroup>
            </NewReplyBoxWrap>
          </animated.div>
        </FormWrapper>
      )}
    </Formik>
  );
};

NewReplyBox.propTypes = {
  feedObject: PropTypes.instanceOf(Object).isRequired,
  isCommentClicked: PropTypes.bool.isRequired,
  commentObject: PropTypes.instanceOf(Object).isRequired,
  showReplies: PropTypes.bool.isRequired,
  setShowReplies: PropTypes.func.isRequired,
};

export default NewReplyBox;
